import type { Theme, Components } from '@mui/material/styles';

import { inputBaseClasses } from '@mui/material/InputBase';
import { filledInputClasses } from '@mui/material/FilledInput';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

import { varAlpha } from '../../styles';

// ----------------------------------------------------------------------

const inputTransitions = (theme: Theme) =>
  theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shortest,
  });

const fieldShadow = (theme: Theme) =>
  `0 1px 2px 0 ${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)}`;

const focusShadow = (theme: Theme, color = theme.vars.palette.primary.mainChannel) =>
  `0 0 0 3px ${varAlpha(color, 0.16)}`;

// ----------------------------------------------------------------------

const MuiInputBase: Components<Theme>['MuiInputBase'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      color: theme.vars.palette.text.primary,
      borderRadius: theme.shape.borderRadius,
      [`&.${inputBaseClasses.disabled}`]: {
        '& svg': { color: theme.vars.palette.text.disabled },
      },
      [`&.${inputBaseClasses.focused}`]: {
        '& svg': { color: theme.vars.palette.text.secondary },
      },
      [`& .${inputBaseClasses.input}:focus`]: {
        borderRadius: 'inherit',
      },
    }),
    input: ({ theme }) => ({
      fontSize: theme.typography.pxToRem(15),
      [theme.breakpoints.down('sm')]: {
        // This will prevent zoom in Safari min font size ~ 16px
        fontSize: theme.typography.pxToRem(16),
      },
      '&::placeholder': {
        opacity: 1,
        color: theme.vars.palette.text.disabled,
      },
      '&:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset`,
        WebkitTextFillColor: theme.vars.palette.text.primary,
        caretColor: theme.vars.palette.text.primary,
        borderRadius: 'inherit',
      },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiInput: Components<Theme>['MuiInput'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    underline: ({ theme }) => ({
      '&::before': { borderBottomColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.28) },
      '&:hover:not(.Mui-disabled, .Mui-error)::before': {
        borderBottomColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.56),
      },
      '&::after': { borderBottomColor: theme.vars.palette.primary.main },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiOutlinedInput: Components<Theme>['MuiOutlinedInput'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.vars.palette.background.paper,
      boxShadow: fieldShadow(theme),
      transition: inputTransitions(theme),
      '&:hover': {
        backgroundColor: theme.vars.palette.background.paper,
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.4),
        },
      },
      [`&.${outlinedInputClasses.focused}`]: {
        backgroundColor: theme.vars.palette.background.paper,
        boxShadow: focusShadow(theme),
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.vars.palette.primary.main,
          borderWidth: 1,
        },
      },
      [`&.${outlinedInputClasses.error}`]: {
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.04),
        boxShadow: 'none',
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: varAlpha(theme.vars.palette.error.mainChannel, 0.56),
        },
        [`&.${outlinedInputClasses.focused}`]: {
          boxShadow: focusShadow(theme, theme.vars.palette.error.mainChannel),
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.vars.palette.error.main,
          },
        },
      },
      [`&.${outlinedInputClasses.disabled}`]: {
        backgroundColor: theme.vars.palette.action.disabledBackground,
        boxShadow: 'none',
        [`& .${outlinedInputClasses.notchedOutline}`]: {
          borderColor: 'transparent',
        },
      },
    }),
    notchedOutline: ({ theme }) => ({
      borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
      transition: inputTransitions(theme),
    }),
  },
};

// ----------------------------------------------------------------------

const MuiFilledInput: Components<Theme>['MuiFilledInput'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { disableUnderline: true },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      boxSizing: 'border-box',
      borderRadius: theme.shape.borderRadius,
      border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.16)}`,
      backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.04),
      boxShadow: fieldShadow(theme),
      transition: inputTransitions(theme),
      '&:hover': {
        borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.32),
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.06),
      },
      [`&.${filledInputClasses.focused}`]: {
        borderColor: theme.vars.palette.primary.main,
        backgroundColor: theme.vars.palette.background.paper,
        boxShadow: focusShadow(theme),
      },
      [`&.${filledInputClasses.error}`]: {
        borderColor: varAlpha(theme.vars.palette.error.mainChannel, 0.56),
        backgroundColor: varAlpha(theme.vars.palette.error.mainChannel, 0.04),
        boxShadow: 'none',
        [`&.${filledInputClasses.focused}`]: {
          borderColor: theme.vars.palette.error.main,
          backgroundColor: theme.vars.palette.background.paper,
          boxShadow: focusShadow(theme, theme.vars.palette.error.mainChannel),
        },
      },
      [`&.${filledInputClasses.disabled}`]: {
        backgroundColor: theme.vars.palette.action.disabledBackground,
        borderColor: 'transparent',
        boxShadow: 'none',
      },
    }),
  },
};

// ----------------------------------------------------------------------

export const textfield = {
  MuiInput,
  MuiInputBase,
  MuiFilledInput,
  MuiOutlinedInput,
};
